import React, { useCallback } from 'react';
import { useTranslation } from 'i18n';
import { Grid, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import EventJLiveRequiredPassportIcon from 'components/svg/EventJLiveRequiredPassportIcon.svg';
import Button from 'components/common/Button/Button';
import { useSelector } from 'react-redux';
import useModal from '../../hooks/useModal';
import AuthModal from '../auth/AuthModal';
import JLivePassportVerificationModal from '../JLivePassport/JLivePassortVerificationModal';
import { useAuthProps } from '../../hooks';
import { AuthSelectors } from '../../store/selectors';
import EventCollapsedInfo from './EventCollapsedInfo';
import { setGettingPassportInitiate } from './utils';

interface IProps {
  event?: any;
  collapsed?: boolean;
  onJLivePassportCreated: () => void;
  onSuccessfullyProceed: () => void;
  price: string;
}

interface StyleProps {
  collapsed?: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme, params) => ({
  requiredPassportContainer: {
    textTransform: 'none',
    marginTop: 8,
    padding: '24px 12px 12px 12px',
    backgroundColor: theme.palette.custom.gray.extraLight,
    borderRadius: 8,
    '@media (max-width: 1187px)': {
      marginTop: 0,
      borderRadius: 0,
      padding: 16,
      height: 80,
    },
    '@media (max-width: 600px)': {
      height: 128,
    },
    ...(params?.collapsed && {
      marginTop: 0,
      padding: 16,
      borderRadius: 0,
      height: 80,
    }),
  },
  priceWrapper: {
    paddingLeft: 64,
    marginLeft: 0,
    '@media (max-width: 991px)': {
      paddingLeft: 0,
      marginLeft: 'auto',
    },
    '@media (max-width: 930px)': {
      paddingLeft: 64,
      marginLeft: 0,
    },
    '@media (max-width: 745px)': {
      paddingLeft: 16,
      marginLeft: 0,
    },
    '@media (max-width: 672px)': {
      paddingLeft: 0,
      marginLeft: -16,
    },
  },
  priceWrapperSquareView: {
    position: 'absolute',
    top: -36,
    left: 0,
    padding: 4,
    backgroundColor: '#4478FE',
  },
}));

const EventPassportRequiredCard: React.FC<React.PropsWithChildren<IProps>> = ({
  event,
  collapsed,
  onJLivePassportCreated,
  onSuccessfullyProceed,
  price,
}) => {
  const [t] = useTranslation('public');
  const { classes } = useStyles({ collapsed });
  const { isAuthenticated, isHardUser } = useAuthProps();
  const [showAuthModal] = useModal(AuthModal);
  const [showJLivePassportVerificationModal] = useModal(JLivePassportVerificationModal);
  const user = useSelector(AuthSelectors.currentUser);
  const less1187 = useMediaQuery('(max-width: 1187px)');
  const less930 = useMediaQuery('(max-width: 930px)');
  const less600 = useMediaQuery('(max-width: 600px)');

  const onSuccessfullyJoinProceed = useCallback(() => {
    showJLivePassportVerificationModal({
      addToHashParams: true,
      variant: user?.is_phone_verified ? 'profileUpdates' : 'phone',
    });
  }, [showJLivePassportVerificationModal, user?.is_phone_verified]);

  const handleGetJLivePassport = () => {
    if (isAuthenticated) {
      if (isHardUser) {
        showJLivePassportVerificationModal({
          onSuccess: () => onJLivePassportCreated(),
          variant: user?.is_phone_verified ? 'profileUpdates' : 'phone',
        });
      } else {
        showAuthModal({
          variant: 'join',
          onSuccessfullyProceed: onSuccessfullyJoinProceed,
        });
      }
    } else {
      setGettingPassportInitiate();
      showAuthModal({ variant: 'login', onSuccessfullyProceed });
    }
  };

  const onLoginClick = () => {
    setGettingPassportInitiate();
    showAuthModal({ variant: 'login' });
  };

  const line1 = (justifyContent: string) => (
    <Grid container spacing={0.5} alignItems="center" justifyContent={justifyContent}>
      <Grid item display="flex" alignItems="center">
        <EventJLiveRequiredPassportIcon />
      </Grid>
      <Grid item>
        <Typography variant="body1" color="textPrimary" fontWeight={500} marginTop={-0.375}>
          required to register
          {/* {t<string>('screen.event.requiredPassportCard.requiredToRegister')} */}
        </Typography>
      </Grid>
    </Grid>
  );

  const line2 = (
    <Typography variant="body2" color="textPrimary" fontWeight={400}>
      Its FREE and takes 2 minutes.
      {/* {t<string>('screen.event.requiredPassportCard.itsFree2min')} */}
    </Typography>
  );

  const button = (
    <Button
      variant="contained"
      rounded
      color="primary"
      style={{ fontWeight: 500, height: 40 }}
      onClick={handleGetJLivePassport}
    >
      Get JLive Passport
      {/* {t<string>('screen.event.requiredPassportCard.button.getJLivePassport')} */}
    </Button>
  );

  const line3 = (
    <Grid item xs={12} style={{ marginTop: 14 }}>
      {!isAuthenticated && (
        <Grid container spacing={0.5} alignItems="center" justifyContent="center">
          <Grid item display="flex" alignItems="center">
            <Typography variant="body2" fontWeight={400} marginTop={-0.375}>
              Already have a Jlive Passport?
              {/* {t<string>('screen.event.requiredPassportCard.alreadyHavePassport')} */}
            </Typography>
          </Grid>

          <Grid item>
            <Typography
              style={{ cursor: 'pointer' }}
              color="primary"
              variant="body2"
              fontWeight={700}
              marginTop={-0.375}
              onClick={onLoginClick}
            >
              {t<string>('common:button.login')}
            </Typography>
          </Grid>
        </Grid>
      )}
    </Grid>
  );

  const squareView = (
    <>
      {less600 && (
        <Grid container className={classes.priceWrapperSquareView} justifyContent="center">
          <Grid item>
            <Typography variant="h6" color="white">
              {price}
            </Typography>
          </Grid>
        </Grid>
      )}
      <Grid container spacing={less600 ? 0 : 1} alignItems="center" justifyContent="center">
        <Grid item>
          <Grid container alignItems="center" justifyContent="center">
            <Grid item xs={12}>
              {line1('center')}
            </Grid>
            <Grid item style={{ paddingTop: less600 ? 4 : 16 }}>
              {line2}
            </Grid>
          </Grid>
        </Grid>
        <Grid item style={{ paddingTop: less600 ? 12 : 16 }}>
          {button}
        </Grid>
        {!less930 && !collapsed && line3}
      </Grid>
    </>
  );

  const lineViewBig = (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid item className={classes.priceWrapper}>
        <Typography variant="h6" color="textPrimary">
          {price}
        </Typography>
      </Grid>
      <Grid item>
        <Grid container spacing={4.5} alignItems="center" justifyContent="flex-end">
          <Grid item>
            <Grid container alignItems="center">
              <Grid item xs={12}>
                {line1('flex-end')}
              </Grid>
              <Grid item xs={12} style={{ paddingTop: 4, display: 'flex', justifyContent: 'flex-end' }}>
                {line2}
              </Grid>
            </Grid>
          </Grid>
          <Grid item>{button}</Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  const collapsedView = (
    <Grid container alignItems="center" justifyContent="space-between" style={{ paddingLeft: 34, paddingRight: 48 }}>
      <Grid item>
        <EventCollapsedInfo event={event} />
      </Grid>
      <Grid item>
        <Grid container spacing={4.5} alignItems="center" justifyContent="flex-end">
          <Grid item className={classes.priceWrapper}>
            <Typography variant="h6" color="textPrimary">
              {price}
            </Typography>
          </Grid>
          <Grid item style={{ paddingLeft: 40 }}>
            <Grid container alignItems="center" direction="column">
              <Grid item xs>
                {line1('flex-end')}
              </Grid>
              <Grid item xs>
                <Button
                  variant="contained"
                  rounded
                  color="primary"
                  style={{ fontWeight: 500, height: 40, width: 244 }}
                  onClick={handleGetJLivePassport}
                >
                  Get JLive Passport
                  {/* {t<string>('screen.event.requiredPassportCard.button.getJLivePassport')} */}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  const lineViewSmall = (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid item className={classes.priceWrapper}>
        <Typography variant="h6" color="textPrimary">
          {price}
        </Typography>
      </Grid>
      <Grid item>
        <Grid container spacing={4.5} alignItems="center" justifyContent="space-between">
          <Grid item style={{ maxWidth: 324 }}>
            <Grid container alignItems="center">
              <Grid item xs={12} style={{ paddingLeft: 24 }}>
                {line1('flex-start')}
              </Grid>
              <Grid
                item
                xs={12}
                style={{ paddingTop: 4, paddingLeft: 24, display: 'flex', justifyContent: 'flex-start' }}
              >
                {line2}
              </Grid>
            </Grid>
          </Grid>
          <Grid item>{button}</Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <div
      id="passport-required-section"
      className={classes.requiredPassportContainer}
      {...(collapsed && !less1187 && { style: { height: 96 } })}
    >
      {((!collapsed && !less1187) || less600) && squareView}
      {collapsed && !less1187 && collapsedView}
      {less1187 && !less930 && lineViewBig}
      {less930 && !less600 && lineViewSmall}
    </div>
  );
};

export default EventPassportRequiredCard;
